<template>
  <button
    aria-haspopup="dialog"
    aria-label="Order Free Swatches"
    class="order-swatches-button"
    :class="isMcom ? 'link-med' : 'link-sm'"
    @click.stop="handleOrderSwatchesClick"
  >
    {{ orderSwatchesText }}
  </button>
</template>

<script setup>
import { defineAsyncComponent, markRaw } from 'vue';
import { storeToRefs } from 'pinia';
import { useColor } from '@nuxt-common/media-card-large/app/modules/color';
import { useType as useOrientation } from '~/modules/type/composables/useType';
import useSize from '~/modules/size/composables/useSize';
import { useBrand } from '~/composables/useBrand';
import { useSlideout } from '~/composables/utils/useSlideout';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { isEmpty } from '~/utils/commonUtils';
import { FIRST_ORIENTATION_TYPE_ID } from '../constants/orientationType';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';

const { productId: mainProductId, productData } = useProduct();
const { selectedMember, memberProduct } = storeToRefs(useConsolidatedMemberStore());
const { isShopTheSetExpEnabled } = useFeatureEligibility();

const productId = computed(() => (isShopTheSetExpEnabled ? selectedMember.value : mainProductId));
const product = computed(() => (isShopTheSetExpEnabled ? memberProduct.value : productData));

const { isMcom } = useBrand();
const { showSlideout } = useSlideout();
const { colors } = useColor(productId);
const orderSwatchesText = isMcom ? 'Order Free Swatches' : 'Order Swatches';
const selectedPrice = useProductPrice();
const { selectedTypeId: selectedOrientationId } = useOrientation(productId);
const { selectedSizeId } = useSize();

const hasSize = () => !isEmpty(product.value?.traits?.sizes?.sizeMap);
const hasOrientation = () => !isEmpty(product.value?.traits?.types?.typeMap);

/**
 * @description If customer hasn't selected size/orientation, we default to the first option (index 0)
 * when the OrderSwatchesInfo.vue slideout opens. Without this, getItemsArray() returns empty upcId
 * and upcNumber causing POST request for ordering swatches to fail.
 */
const preselectOrientationId = () => {
  if (hasOrientation() && selectedOrientationId.value === null) {
    // reflect change on UI and show the left orientation type as selected
    selectedOrientationId.value = FIRST_ORIENTATION_TYPE_ID;
  } else if (hasSize()) {
    // we change selectedOrientationId.value because changing selectedSizeId.value
    // would reflect that change on UI which we don't want because of requirements
    selectedOrientationId.value = selectedSizeId.value === null ? FIRST_ORIENTATION_TYPE_ID : selectedSizeId.value;
  }
};

const openSwatchesDrawer = () => {
  const slideout = {
    id: 'Order Swatches',
    title: 'Order free swatches',
    component: markRaw(
      defineAsyncComponent(
        /* webpackChunkName: "OrderSwatchesInfo" */
        () => import('./slideoutContent/OrderSwatchesInfo.vue'),
      ),
    ),
    props: {
      productId: productId.value,
      product: product.value,
      colors: colors.value,
      selectedPrice: selectedPrice.value,
      selectedOrientationId: selectedOrientationId.value,
    },
  };

  showSlideout(slideout);
};

const handleOrderSwatchesClick = () => {
  if (!isMcom && (hasOrientation() || hasSize())) {
    preselectOrientationId();
  }
  openSwatchesDrawer();
};
</script>

<style lang="scss" brand="mcom" scoped>
.order-swatches-button {
  color: $dashboard-color;
}
</style>
